const { shuffle, getRandomElement } = require("../../../../../shared/src/ranj/util/RandomUtil");

class PuzzleRandomizer {

    /**
     * Generate randomized rule index
     * @param {Array} randomizedPuzzles See game.json
     * @returns {number} ruleIndex
     */
    static generateRuleIndex(randomizedPuzzles) {
        return Math.floor(Math.random() * randomizedPuzzles[0].rulesPerRuleset.length);
    }

    /**
     * Generate randomized levels
     * @param {Array} puzzleConfigs See game.json
     * @param {number} ruleSetIndex Index of ruleset to use. All levels will use the same ruleset. 
     * @param {number} spotCount Number of spots per level. May vary between practice and normal levels
     * @param {boolean} isPractice Whether to generate practice levels or not. Some logic is slightly different for practice levels.
     * @param {Object} overrides Optional overrides for rules, locations and spots, to use fixed rule (index), location (id) and spots (indexes) instead of random.
     * @returns 
     */
    static generatePuzzles(puzzleConfigs, locations, ruleSetIndex, spotCount, isPractice, overrides = {}) {
        if (overrides.location != null && availableLocations.includes(overrides.location)) {
            warn('Using fixed location by id:', location);
        }

        const getSolutionString = (solution, correctOrderRequired) => {
            if (!correctOrderRequired) {
                solution = solution.slice().sort();
            }
            return solution.join(",");
        }

        const hasDifferentRule = (config, ruleIds) => config.rulesPerRuleset[ruleSetIndex].some(ruleId => !ruleIds.includes(ruleId));


        // Randomize locations for puzzles so that:
        // - locations are distributed as good as possible across levels
        // - no 2 consecutive levels have the same location if possible (not possible for practice levels currently).
        // Just shuffle locations and repeat until we have enough, but check if no repetitions occur.
        const locationsPerLevel = [];
        while (locationsPerLevel.length < puzzleConfigs.length) {
            let locationsToAdd;
            do {
                locationsToAdd = shuffle(locations);
            } while (locations.length > 1 && locationsToAdd[0] == locationsPerLevel.at(-1));
            locationsPerLevel.push(...locationsToAdd);
        }

        let puzzlePrev;
        return puzzleConfigs.map((puzzleConfig, idx) => {
            let puzzle;
            const lastRuleIds = puzzlePrev ? puzzlePrev.rule.id : []; // NB: rule.id is an array!
            const lastSolutionStrings = puzzlePrev ? puzzlePrev.rule.correctInput.map(solution => getSolutionString(solution, puzzlePrev.correctOrderRequired)) : [];

            let location = locationsPerLevel[idx];
            if (overrides.location != null && availableLocations.includes(overrides.location)) {
                location = overrides.location;
                warn(`Puzzle ${puzzleConfig._comment}: Using fixed location by id: ${overrides.location}, ${location}`);
            }

            while (true) {
                puzzle = PuzzleRandomizer.generatePuzzle(puzzleConfig, locationsPerLevel[idx], ruleSetIndex, spotCount, isPractice, overrides);
                // Some checks to ensure consecutive puzzles are not alike:
                // 1.Ensure no 2 consecutive puzzles have the same solution (in terms of spots):
                //  This means that solution of first attempt of current puzzle should be different from all attempts of previous puzzle
                //  and solutions of first and second attempt of current puzzle should be different from each other.
                //  This last requirement could also be enforced within the generatePuzzle method, but this way it is kept in one place.
                const firstSolutionString = getSolutionString(puzzle.rule.correctInput[0], puzzle.correctOrderRequired);
                if (lastSolutionStrings.includes(firstSolutionString)) {
                    log(`Found repeating solution '${firstSolutionString}' in puzzle '${puzzle._comment}' (wrt prev puzzle). Regenerating.`);
                    continue;
                }
                if (puzzle.rule.length > 1 && firstSolutionString == getSolutionString(puzzle.rule[1], puzzle.correctOrderRequired)) {
                    log(`Found repeating solution '${firstSolutionString}' in puzzle '${puzzle._comment}' (in second sequence). Regenerating.`);
                    continue;
                }
                // 2. Ensure no 2 consecutive puzzles have the exact same rule (if possible)
                //   Whether this is possible should be checked from the config.
                //   NB: within a puzzle this is already enforced by the generatePuzzle method.
                const firstRuleId = puzzle.rule.id[0];
                if (lastRuleIds.includes(firstRuleId) && hasDifferentRule(puzzleConfig, lastRuleIds)) {
                    log(`Found repeating rule '${firstRuleId}' in puzzle '${puzzle._comment}'. Regenerating.`);
                    continue;
                }
                break;
            }
            puzzlePrev = puzzle;
            return puzzle;
        });

    }

    /**
     * Generate randomized level
     * @param {Object} puzzleConfig See game.json
     * @param {number} ruleSetIndex Index of ruleset to use. All levels will use the same ruleset. 
     * @param {number} spotCount Number of spots per level. May vary between practice and normal levels
     * @param {boolean} isPractice Whether to generate practice levels or not. Some logic is slightly different for practice levels.
     * @param {Object} overrides Optional overrides for rules, locations and spots, to use fixed rule (index), location (id) and spots (indexes) instead of random.
     * @returns 
     */
    static generatePuzzle(puzzleConfig, location, ruleSetIndex, spotCount, isPractice, overrides = {}) {

        // NB: Conerning overrides:
        //   We first pick randomly, then overwrite with overrides if they are set.
        //   This way, a seeded PRNG will still generate the same values for other choices, 
        //   because the order / amount of calls is not changed.

        // Per level, multiple lists of available rules are defined. Each level uses the same index, to ensure a balanced spread of rules across levels.
        const availableRulesInRuleset = puzzleConfig.rulesPerRuleset[ruleSetIndex];

        const sequences = [];
        const ruleIds = [];

        // A level can have 1 or 2 sequence entries. If there are 2, that means the second sequence is a retry.
        for (let idx = 0; idx < puzzleConfig.attempts; idx++) {
            // Determine rule id                
            let ruleId;
            if (idx == 0) {
                // First try: pick random rule
                ruleId = getRandomElement(availableRulesInRuleset);
            } else {
                // Retry: use same rule as first try if it is a order/quantity rule,
                // but use a different pattern if it is a pattern rule.
                if (isPatternRule(ruleIds[0])) {
                    ruleId = getRandomElement(availableRulesInRuleset, [ruleIds[0]]);
                } else {
                    ruleId = ruleIds[0];
                }
            }
            //  .. or override for testing:
            if (overrides.rule != null) {
                ruleId = availableRulesInRuleset[Math.min(availableRulesInRuleset.length - 1, overrides.rule)];
                warn(`Puzzle ${puzzleConfig._comment}: Using fixed rule by index: ${overrides.rule}, ${ruleId}`);
            }

            const sequenceLength = puzzleConfig.sequenceLength;
            let spots = generateUniqueValues(sequenceLength, 1, spotCount);
            if (overrides.spots != null) {
                // use specified spot values and fill up with random spots
                const spotsOverride = overrides.spots.filter(spot => spot >= 1 && spot <= spotCount);
                spots = [...spotsOverride];
                while (spots.length < sequenceLength) {
                    const randomSpot = getRandomElement(spots, spots);
                    spots.push(randomSpot);
                }
                warn(`Puzzle ${puzzleConfig._comment}: fixed spots (but filling up with random spots): ${spotsOverride}, ${spots}`);
            }

            var requiredPattern = null;
            if (isPatternRule(ruleId)) {
                requiredPattern = ruleId.replace('locatePattern', '');
            }
            const patterns = generateUniquePatterns(sequenceLength, requiredPattern);

            const amountsPool = Array.from({length: sequenceLength}, (_, idx) => idx + 1);
            var amounts = shuffle(amountsPool);
            log(`Puzzle ${puzzleConfig._comment}: randomized amounts for '${ruleId}': ${amounts}`);

            const sequence = amounts.map((amount, idx) => {
                return {
                    spot: spots[idx],
                    variation: {
                        amount,
                        pattern: patterns[idx]
                    }
                }
            });

            sequences.push(sequence);
            ruleIds.push(ruleId);
        };

        const correctInput = sequences.map((seq, idx) => PuzzleRandomizer.generateCorrectInput(ruleIds[idx], seq));
        const correctOrderRequired = !isPatternRule(ruleIds[0]);

        return {
            _comment: puzzleConfig._comment,
            location,
            time: puzzleConfig.time,
            allowFail: puzzleConfig.allowFail,
            successFeedback: puzzleConfig.successFeedback,
            sequence: sequences,
            rule: {
                id: ruleIds,
                correctInput,
                correctOrderRequired
            }
        };
    }

    static generateCorrectInput(rule, sequence) {
        return getCorrectOrderOfSequenceForRule(sequence, rule).map(item => item.spot);
    }
}

const isQuantityRule = ruleId => ['smallToBig', 'bigToSmall'].includes(ruleId);
const isPatternRule = ruleId => ruleId.startsWith('locatePattern');

const getCorrectOrderOfSequenceForRule = (sequence, rule) => {
    sequence = sequence.slice();
    switch (rule) {
        case 'smallToBig':
            return sequence.sort((a, b) => a.variation.amount - b.variation.amount);
        case 'bigToSmall':
            return sequence.sort((a, b) => b.variation.amount - a.variation.amount);
        case 'reverseOrder':
            return sequence.reverse();
        default:
            if (isPatternRule(rule)) {
                const pattern = rule.replace('locatePattern', '');
                return sequence.filter(item => item.variation.pattern === pattern);
            }
            return [];
    }
}

/**
 * Generate a random array of count integer values starting at min up to max (inclusive)
 * @param {number} count 
 * @param {number} max 
 * @returns 
 */
const generateUniqueValues = (count, min, max) => {
    if (count > max) {
        throw new Error('Count cannot be greater than max');
    }
    return shuffle(Array(max).fill(0).map((_, i) => i + min)).slice(0, count);
}

const generateUniquePatterns = (count, pattern = null) => {
    const patterns = 'ABCDEF'.split('').filter(p => p !== pattern);
    const shuffled = [];
    if (pattern) {
        shuffled.push(pattern);
    }
    while (count > shuffled.length) {
        shuffled.push(...shuffle(patterns));
    }
    // slice correct amount and shuffle so specified pattern is not always at the same index
    return shuffle(shuffled.slice(0, count));
}


const log = str => {
    if (typeof ranj !== "undefined" && ranj.DEBUG) {
        console.log(str);
    }
}

const warn = str => {
    if (typeof ranj !== "undefined" && ranj.DEBUG) {
        console.warn(str);
    }
}


module.exports = PuzzleRandomizer;